import { yupResolver } from '@hookform/resolvers/yup'
import { find } from 'lodash-es'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import * as yup from 'yup'

import { SettingsColors } from '../../../common/models'
import FormGroup from '../../../core/components/FormGroup'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  getSettingsColors,
  updateSettingsColors,
} from '../../../core/services/festival-settings'
import { useAppSelector } from '../../../store'
import { MessageBox } from '../messages/MessageBox'
import { Header, PageTitle } from '@/components'
import { Button, Card, Input } from '@/components/ui'

interface ITicketColorValues {
  from: string
  to: string
  fallBackColor: string
}

interface IMainColorsValues {
  mainColor1: string
  mainColor2: string
  mainColor3: string
  mainColor4: string
  mainColor5: string
  mainColor6: ITicketColorValues
}

interface ISecondaryColorsValues {
  gradiant1: ITicketColorValues
  gradiant2: ITicketColorValues
  gradiant3: ITicketColorValues
  gradiant4: ITicketColorValues
  gradiant5: ITicketColorValues
  gradiant6: ITicketColorValues
  gradiant7: ITicketColorValues
}

interface ITertiaryColorsValues {
  gradiantFade: ITicketColorValues
  gradiantMenu: ITicketColorValues
}

export const CustomThemePage: React.FC = () => {
  const navigate = useNavigate()
  const { editionId, festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const [settingsColor, setSettingsColor] = useState<SettingsColors[]>()

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const { hasEditPermission } = useCheckPermission({ page: 'SETTINGS' })

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const response = await getSettingsColors()
        setSettingsColor(response.data)
        const ticket = find(response.data, data => data.name === 'TICKET')
        const main = find(response.data, data => data.name === 'PRIMARY')
        const secondary = find(response.data, data => data.name === 'SECONDARY')
        const ternary = find(response.data, data => data.name === 'TERTIARY')

        setTimeout(() => {
          if (ticket?.gradients) {
            setTicketColorValue(`from`, ticket.gradients?.[0]?.colors?.[0].color?.value.substring(0, 7))
            setTicketColorValue(`to`, ticket.gradients?.[0]?.colors?.[1].color?.value.substring(0, 7))
            setTicketColorValue(`fallBackColor`, ticket.gradients?.[0]?.fallColor.value.substring(0, 7))
          }
          if (main?.gradients && main?.colors) {
            setMainColorValues(`mainColor1`, main.colors?.[0]?.value.substring(0, 7))
            setMainColorValues(`mainColor2`, main.colors?.[1]?.value.substring(0, 7))
            setMainColorValues(`mainColor3`, main.colors?.[2]?.value.substring(0, 7))
            setMainColorValues(`mainColor4`, main.colors?.[3]?.value.substring(0, 7))
            setMainColorValues(`mainColor5`, main.colors?.[4]?.value.substring(0, 7))
            setMainColorValues(`mainColor6.from`, main.gradients?.[0]?.colors?.[0]?.color?.value.substring(0, 7))
            setMainColorValues(`mainColor6.to`, main.gradients?.[0]?.colors?.[1]?.color?.value.substring(0, 7))
            setMainColorValues(`mainColor6.fallBackColor`, main.gradients?.[0]?.fallColor?.value.substring(0, 7))
          }
          if (secondary?.gradients) {
            setSecondaryColorsValue(`gradiant1.from`, secondary.gradients?.[0]?.colors?.[0]?.color?.value.substring(0, 7))
            setSecondaryColorsValue(`gradiant1.to`, secondary.gradients?.[0]?.colors?.[1]?.color?.value.substring(0, 7))
            if (secondary.gradients?.[0]?.fallColor?.value) {
              setSecondaryColorsValue(`gradiant1.fallBackColor`, secondary.gradients?.[0]?.fallColor?.value?.substring(0, 7))
            }
            setSecondaryColorsValue(`gradiant2.from`, secondary.gradients?.[1]?.colors?.[0]?.color?.value.substring(0, 7))
            setSecondaryColorsValue(`gradiant2.to`, secondary.gradients?.[1]?.colors?.[1]?.color?.value.substring(0, 7))
            if (secondary.gradients?.[1]?.fallColor?.value) {
              setSecondaryColorsValue(`gradiant2.fallBackColor`, secondary.gradients?.[1]?.fallColor?.value.substring(0, 7))
            }
            setSecondaryColorsValue(`gradiant3.from`, secondary.gradients?.[2]?.colors?.[0]?.color?.value.substring(0, 7))
            setSecondaryColorsValue(`gradiant3.to`, secondary.gradients?.[2]?.colors?.[1]?.color?.value.substring(0, 7))
            if (secondary.gradients?.[2]?.fallColor?.value) {
              setSecondaryColorsValue(`gradiant3.fallBackColor`, secondary.gradients?.[2]?.fallColor?.value.substring(0, 7))
            }
            setSecondaryColorsValue(`gradiant4.from`, secondary.gradients?.[3]?.colors?.[0]?.color?.value.substring(0, 7))
            setSecondaryColorsValue(`gradiant4.to`, secondary.gradients?.[3]?.colors?.[1]?.color?.value.substring(0, 7))
            if (secondary.gradients?.[3]?.fallColor?.value) {
              setSecondaryColorsValue(`gradiant4.fallBackColor`, secondary.gradients?.[3]?.fallColor?.value.substring(0, 7))
            }
            setSecondaryColorsValue(`gradiant5.from`, secondary.gradients?.[4]?.colors?.[0]?.color?.value.substring(0, 7))
            setSecondaryColorsValue(`gradiant5.to`, secondary.gradients?.[4]?.colors?.[1]?.color?.value.substring(0, 7))
            if (secondary.gradients?.[4]?.fallColor?.value) {
              setSecondaryColorsValue(`gradiant5.fallBackColor`, secondary.gradients?.[4]?.fallColor?.value.substring(0, 7))
            }
            setSecondaryColorsValue(`gradiant6.from`, secondary.gradients?.[5]?.colors?.[0]?.color?.value.substring(0, 7))
            setSecondaryColorsValue(`gradiant6.to`, secondary.gradients?.[5]?.colors?.[1]?.color?.value.substring(0, 7))
            if (secondary.gradients?.[5]?.fallColor?.value) {
              setSecondaryColorsValue(`gradiant6.fallBackColor`, secondary.gradients?.[5]?.fallColor?.value.substring(0, 7))
            }
            setSecondaryColorsValue(`gradiant7.from`, secondary.gradients?.[6]?.colors?.[0]?.color?.value.substring(0, 7))
            setSecondaryColorsValue(`gradiant7.to`, secondary.gradients?.[6]?.colors?.[1]?.color?.value.substring(0, 7))
            if (secondary.gradients?.[6]?.fallColor?.value) {
              setSecondaryColorsValue(`gradiant7.fallBackColor`, secondary.gradients?.[6]?.fallColor?.value.substring(0, 7))
            }
          }
          if (ternary?.gradients) {
            setTertiaryColorsValue(`gradiantFade.from`, ternary.gradients?.[0]?.colors?.[0]?.color?.value.substring(0, 7))
            setTertiaryColorsValue(`gradiantFade.to`, ternary.gradients?.[0]?.colors?.[1]?.color?.value.substring(0, 7))
            if (ternary.gradients?.[0]?.fallColor?.value) {
              setTertiaryColorsValue(`gradiantFade.fallBackColor`, ternary.gradients?.[0]?.fallColor?.value.substring(0, 7))
            }
            setTertiaryColorsValue(`gradiantMenu.from`, ternary.gradients?.[1]?.colors?.[0]?.color?.value.substring(0, 7))
            setTertiaryColorsValue(`gradiantMenu.to`, ternary.gradients?.[1]?.colors?.[1]?.color?.value.substring(0, 7))
            if (ternary.gradients?.[1]?.fallColor?.value) {
              setTertiaryColorsValue(`gradiantMenu.fallBackColor`, ternary.gradients?.[1]?.fallColor?.value.substring(0, 7))
            }
          }
        }, 0)
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  const gradiantShape = {
    from: yup.string().required('Field is required'),
    to: yup.string().required('Field is required'),
    fallBackColor: yup.string().required('Field is required'),
  }

  const ticketColorsSchema = yup.object().shape(gradiantShape)

  const {
    handleSubmit: handleSubmitTicketColors,
    formState: { errors: ticketColorsErrors },
    register: registerTicketColors,
    watch,
    setValue: setTicketColorValue,
  } = useForm<ITicketColorValues>({
    mode: 'onChange',
    resolver: yupResolver<ITicketColorValues>(ticketColorsSchema),
  })

  const from = watch('from')
  const to = watch('to')

  const submitTicketColors = async (values: ITicketColorValues) => {
    const arr: SettingsColors[] = []
    const ticket = find(settingsColor, (data) => data.name === 'TICKET')
    if (ticket?.gradients) {
      ticket.gradients[0].fallColor.value = values.fallBackColor
      ticket.gradients[0].colors[0].color.value = values.from
      ticket.gradients[0].colors[1].color.value = values.to
    }
    if (ticket) arr.push(ticket)
    const main = find(settingsColor, (data) => data.name === 'PRIMARY')
    if (main) arr.push(main)
    const secondary = find(settingsColor, (data) => data.name === 'SECONDARY')
    if (secondary) arr.push(secondary)
    const ternary = find(settingsColor, (data) => data.name === 'TERTIARY')
    if (ternary) arr.push(ternary)
    try {
      const { status } = await updateSettingsColors(arr)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  const mainColorsSchema = yup.object().shape({
    mainColor1: yup.string().required('Field is required'),
    mainColor2: yup.string().required('Field is required'),
    mainColor3: yup.string().required('Field is required'),
    mainColor4: yup.string().required('Field is required'),
    mainColor5: yup.string().required('Field is required'),
    mainColor6: yup.object().shape(gradiantShape),
  })

  const {
    handleSubmit: handleSubmitMainColors,
    formState: { errors: mainColorsErrors },
    register: registerMainColors,
    watch: watchMainColors,
    setValue: setMainColorValues,
  } = useForm<IMainColorsValues>({
    mode: 'onChange',
    resolver: yupResolver<IMainColorsValues>(mainColorsSchema),
  })

  const mainColor6 = watchMainColors('mainColor6')

  const submitMainColors = async (values: IMainColorsValues) => {
    const arr: SettingsColors[] = []
    const main = find(settingsColor, (data) => data.name === 'PRIMARY')
    if (main?.colors && main.gradients) {
      main.colors[0].value = values.mainColor1
      main.colors[1].value = values.mainColor2
      main.colors[2].value = values.mainColor3
      main.colors[3].value = values.mainColor4
      main.colors[4].value = values.mainColor5
      main.gradients[0].fallColor.value = values.mainColor6.fallBackColor
      main.gradients[0].colors[0].color.value = values.mainColor6.from
      main.gradients[0].colors[1].color.value = values.mainColor6.to
    }
    const ticket = find(settingsColor, (data) => data.name === 'TICKET')
    if (ticket) arr.push(ticket)
    if (main) arr.push(main)
    const secondary = find(settingsColor, (data) => data.name === 'SECONDARY')
    if (secondary) arr.push(secondary)
    const ternary = find(settingsColor, (data) => data.name === 'TERTIARY')
    if (ternary) arr.push(ternary)
    try {
      const { status } = await updateSettingsColors(arr)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  const secondaryColorsSchema = yup.object().shape({
    gradiant1: yup.object().shape(gradiantShape),
    gradiant2: yup.object().shape(gradiantShape),
    gradiant3: yup.object().shape(gradiantShape),
    gradiant4: yup.object().shape(gradiantShape),
    gradiant5: yup.object().shape(gradiantShape),
    gradiant6: yup.object().shape(gradiantShape),
    gradiant7: yup.object().shape(gradiantShape),
  })

  const {
    handleSubmit: handleSecondaryColorsSubmit,
    formState: { errors: secondaryColorsErrors },
    register: registerSecondaryColors,
    watch: watchSecondaryColors,
    setValue: setSecondaryColorsValue,
  } = useForm<ISecondaryColorsValues>({
    mode: 'onChange',
    resolver: yupResolver<ISecondaryColorsValues>(secondaryColorsSchema),
  })

  const gradiant1 = watchSecondaryColors('gradiant1')
  const gradiant2 = watchSecondaryColors('gradiant2')
  const gradiant3 = watchSecondaryColors('gradiant3')
  const gradiant4 = watchSecondaryColors('gradiant4')
  const gradiant5 = watchSecondaryColors('gradiant5')
  const gradiant6 = watchSecondaryColors('gradiant6')
  const gradiant7 = watchSecondaryColors('gradiant7')

  const submitSecondaryColors = async (values: ISecondaryColorsValues) => {
    {
      const arr: SettingsColors[] = []
      const secondary = find(settingsColor, (data) => data.name === 'SECONDARY')
      if (secondary?.gradients) {
        secondary.gradients[0].fallColor.value = values.gradiant1.fallBackColor
        secondary.gradients[0].colors[0].color.value = values.gradiant1.from
        secondary.gradients[0].colors[1].color.value = values.gradiant1.to

        secondary.gradients[1].fallColor.value = values.gradiant2.fallBackColor
        secondary.gradients[1].colors[0].color.value = values.gradiant2.from
        secondary.gradients[1].colors[1].color.value = values.gradiant2.to

        secondary.gradients[2].fallColor.value = values.gradiant3.fallBackColor
        secondary.gradients[2].colors[0].color.value = values.gradiant3.from
        secondary.gradients[2].colors[1].color.value = values.gradiant3.to

        secondary.gradients[3].fallColor.value = values.gradiant4.fallBackColor
        secondary.gradients[3].colors[0].color.value = values.gradiant4.from
        secondary.gradients[3].colors[1].color.value = values.gradiant4.to

        secondary.gradients[4].fallColor.value = values.gradiant5.fallBackColor
        secondary.gradients[4].colors[0].color.value = values.gradiant5.from
        secondary.gradients[4].colors[1].color.value = values.gradiant5.to

        secondary.gradients[5].fallColor.value = values.gradiant6.fallBackColor
        secondary.gradients[5].colors[0].color.value = values.gradiant6.from
        secondary.gradients[5].colors[1].color.value = values.gradiant6.to

        secondary.gradients[6].fallColor.value = values.gradiant7.fallBackColor
        secondary.gradients[6].colors[0].color.value = values.gradiant7.from
        secondary.gradients[6].colors[1].color.value = values.gradiant7.to
      }
      const ticket = find(settingsColor, (data) => data.name === 'TICKET')
      if (ticket) arr.push(ticket)
      const main = find(settingsColor, (data) => data.name === 'PRIMARY')
      if (main) arr.push(main)
      if (secondary) arr.push(secondary)
      const ternary = find(settingsColor, (data) => data.name === 'TERTIARY')
      if (ternary) arr.push(ternary)
      try {
        const { status } = await updateSettingsColors(arr)
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      }
    }
  }

  const tertiaryColorsSchema = yup.object().shape({
    gradiantFade: yup.object().shape(gradiantShape),
    gradiantMenu: yup.object().shape(gradiantShape),
  })

  const {
    handleSubmit: handleTertiaryColorsSubmit,
    formState: { errors: tertiaryColorsErrors },
    register: registerTertiaryColors,
    watch: watchTertiaryColors,
    setValue: setTertiaryColorsValue,
  } = useForm<ITertiaryColorsValues>({
    mode: 'onChange',
    resolver: yupResolver<ITertiaryColorsValues>(tertiaryColorsSchema),
  })

  const gradiantFade = watchTertiaryColors('gradiantFade')
  const gradiantMenu = watchTertiaryColors('gradiantMenu')

  const submitTertiaryColors = async (values: ITertiaryColorsValues) => {
    const arr: SettingsColors[] = []
    const ternary = find(settingsColor, (data) => data.name === 'TERTIARY')
    if (ternary?.gradients) {
      ternary.gradients[0].fallColor.value = values.gradiantFade.fallBackColor
      ternary.gradients[0].colors[0].color.value = values.gradiantFade.from
      ternary.gradients[0].colors[1].color.value = values.gradiantFade.to
      ternary.gradients[1].fallColor.value = values.gradiantMenu.fallBackColor
      ternary.gradients[1].colors[0].color.value = values.gradiantMenu.from
      ternary.gradients[1].colors[1].color.value = values.gradiantMenu.to
    }
    const ticket = find(settingsColor, (data) => data.name === 'TICKET')
    if (ticket) arr.push(ticket)
    const main = find(settingsColor, (data) => data.name === 'PRIMARY')
    if (main) arr.push(main)
    const secondary = find(settingsColor, (data) => data.name === 'SECONDARY')
    if (secondary) arr.push(secondary)
    if (ternary) arr.push(ternary)
    try {
      const { status } = await updateSettingsColors(arr)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  return (
    <>
      <Header pageTitle="Customize Theme" />
      <Card className="p-4 max-h-[calc(100vh-150px)] overflow-y-auto">
        <PageTitle title="Customize Theme" />
        <div className="mt-4">
          <p className="text-title-xxs text-accent-1 mb-2">
            Default Ticket Color
          </p>
          <div className="rounded-3xl border border-monochrome-120">
            <form
              id="settings-ticket-color-form"
              onSubmit={handleSubmitTicketColors(submitTicketColors)}
            >
              <div className="p-3 gap-3">
                <FormGroup label="Gradient">
                  <div className="flex gap-2 w-full">
                    <FormGroup wrapperClassName="w-auto">
                      <div
                        style={{
                          background: `linear-gradient(${from} , ${to})`,
                          width: 30,
                          height: 30,
                          borderRadius: 25,
                          marginTop: 5,
                        }}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Input
                        {...registerTicketColors('from')}
                        type="color"
                        disabled={!hasEditPermission}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Input
                        {...registerTicketColors('to')}
                        type="color"
                        disabled={!hasEditPermission}
                      />
                    </FormGroup>
                    <span className="d-none">{`linear-gradient(${from} , ${to})`}</span>
                  </div>
                </FormGroup>
                <FormGroup label="Fall Color">
                  <Input
                    {...registerTicketColors('fallBackColor')}
                    type="color"
                    disabled={!hasEditPermission}
                  />
                </FormGroup>
              </div>
            </form>
          </div>
          <div className="flex justify-end gap-2 p-3 border-t border-monochrome-120 mt-4">
            <Button
              onClick={() => navigate(-1)}
              variant="secondary"
              data-dismiss="modal"
              disabled={!hasEditPermission}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              form="settings-ticket-color-form"
              type="submit"
              disabled={!hasEditPermission}
            >
              Save
            </Button>
          </div>
        </div>
        <div className="mt-4">
          <p className="text-title-xxs text-accent-1 mb-2">Main Colors</p>
          <div className="rounded-3xl border border-monochrome-120">
            <div className="p-3">
              <form
                id="settings-main-colors-form"
                onSubmit={handleSubmitMainColors(submitMainColors)}
              >
                <div className="flex flex-col gap-3">
                  <div className="p-3 gap-3">
                    <FormGroup label="Main Color 1">
                      <Input
                        isInvalid={!!mainColorsErrors.mainColor1?.message}
                        {...registerMainColors('mainColor1')}
                        type="color"
                        disabled={!hasEditPermission}
                      />
                    </FormGroup>
                  </div>
                  <div className="p-3 gap-3">
                    <FormGroup label="Main Color 2">
                      <Input
                        isInvalid={!!mainColorsErrors.mainColor2?.message}
                        {...registerMainColors('mainColor2')}
                        type="color"
                        disabled={!hasEditPermission}
                      />
                    </FormGroup>
                  </div>
                  <div className="p-3 gap-3">
                    <FormGroup label="Main Color 3">
                      <Input
                        isInvalid={!!mainColorsErrors.mainColor3?.message}
                        {...registerMainColors('mainColor3')}
                        type="color"
                        disabled={!hasEditPermission}
                      />
                    </FormGroup>
                  </div>
                  <div className="p-3 gap-3">
                    <FormGroup label="Main Color 4">
                      <Input
                        isInvalid={!!mainColorsErrors.mainColor4?.message}
                        {...registerMainColors('mainColor4')}
                        type="color"
                        disabled={!hasEditPermission}
                      />
                    </FormGroup>
                  </div>
                  <div className="p-3 gap-3">
                    <FormGroup label="Main Color 5">
                      <Input
                        isInvalid={!!mainColorsErrors.mainColor5?.message}
                        {...registerMainColors('mainColor5')}
                        type="color"
                        disabled={!hasEditPermission}
                      />
                    </FormGroup>
                  </div>
                  <div className="p-3 gap-3">
                    <FormGroup label="Main color 6">
                      <div className="flex gap-2 w-100">
                        <FormGroup wrapperClassName="w-auto">
                          <div
                            style={{
                              background: `linear-gradient(${mainColor6?.from} , ${mainColor6?.to})`,
                              width: 30,
                              height: 30,
                              borderRadius: 25,
                              marginTop: 5,
                            }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Input
                            {...registerMainColors('mainColor6.from')}
                            type="color"
                            disabled={!hasEditPermission}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Input
                            {...registerMainColors('mainColor6.to')}
                            type="color"
                            disabled={!hasEditPermission}
                          />
                        </FormGroup>
                        <span className="d-none">{`linear-gradient(${mainColor6?.from} , ${mainColor6?.to})`}</span>
                      </div>
                    </FormGroup>
                    <FormGroup label="Fall Color">
                      <Input
                        {...registerMainColors('mainColor6.fallBackColor')}
                        type="color"
                        disabled={!hasEditPermission}
                      />
                    </FormGroup>
                  </div>
                </div>
              </form>
            </div>
            <div className="flex justify-end gap-2 p-3 border-t border-monochrome-120 mt-4">
              <Button
                onClick={() => navigate(-1)}
                variant="secondary"
                data-dismiss="modal"
                disabled={!hasEditPermission}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                form="settings-main-colors-form"
                type="submit"
                disabled={!hasEditPermission}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <p className="text-title-xxs text-accent-1 mb-2">Secondary Colors</p>
          <div className="rounded-3xl border border-monochrome-120">
            <form
              id="settings-secondary-colors-form"
              onSubmit={handleSecondaryColorsSubmit(submitSecondaryColors)}
            >
              <div className="flex flex-col gap-3">
                <div className="p-3 gap-3">
                  <FormGroup label="Gradient 1">
                    <div className="flex gap-2 w-full">
                      <FormGroup wrapperClassName="w-auto">
                        <div
                          style={{
                            background: `linear-gradient(${gradiant1?.from} , ${gradiant1?.to})`,
                            width: 30,
                            height: 30,
                            borderRadius: 25,
                            marginTop: 5,
                          }}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Input
                          {...registerSecondaryColors('gradiant1.from')}
                          type="color"
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Input
                          {...registerSecondaryColors('gradiant1.to')}
                          type="color"
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                      <span className="d-none">{`linear-gradient(${gradiant1?.from}, ${gradiant1?.to})`}</span>
                    </div>
                  </FormGroup>
                  <FormGroup label="Fall Color">
                    <Input
                      {...registerSecondaryColors('gradiant1.fallBackColor')}
                      type="color"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                </div>
                <div className="p-3 gap-3">
                  <FormGroup label="Gradient 2">
                    <div className="flex gap-2 w-full">
                      <FormGroup wrapperClassName="w-auto">
                        <div
                          style={{
                            background: `linear-gradient(${gradiant2?.from} , ${gradiant2?.to})`,
                            width: 30,
                            height: 30,
                            borderRadius: 25,
                            marginTop: 5,
                          }}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Input
                          {...registerSecondaryColors('gradiant2.from')}
                          type="color"
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Input
                          {...registerSecondaryColors('gradiant2.to')}
                          type="color"
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                      <span className="d-none">{`linear-gradient(${gradiant2?.from} , ${gradiant2?.to})`}</span>
                    </div>
                  </FormGroup>
                  <FormGroup label="Fall Color">
                    <Input
                      {...registerSecondaryColors('gradiant2.fallBackColor')}
                      type="color"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                </div>
                <div className="p-3 gap-3">
                  <FormGroup label="Gradient 3">
                    <div className="flex gap-2 w-full">
                      <FormGroup wrapperClassName="w-auto">
                        <div
                          style={{
                            background: `linear-gradient(${gradiant3?.from} , ${gradiant3?.to})`,
                            width: 30,
                            height: 30,
                            borderRadius: 25,
                            marginTop: 5,
                          }}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Input
                          {...registerSecondaryColors('gradiant3.from')}
                          type="color"
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Input
                          {...registerSecondaryColors('gradiant3.to')}
                          type="color"
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                      <span className="d-none">{`linear-gradient(${gradiant3?.from} , ${gradiant3?.to})`}</span>
                    </div>
                  </FormGroup>
                  <FormGroup label="Fall Color">
                    <Input
                      {...registerSecondaryColors('gradiant3.fallBackColor')}
                      type="color"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                </div>
                <div className="p-3 gap-3">
                  <FormGroup label="Gradient 4">
                    <div className="flex gap-2 w-full">
                      <FormGroup wrapperClassName="w-auto">
                        <div
                          style={{
                            background: `linear-gradient(${gradiant4?.from} , ${gradiant4?.to})`,
                            width: 30,
                            height: 30,
                            borderRadius: 25,
                            marginTop: 5,
                          }}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Input
                          {...registerSecondaryColors('gradiant4.from')}
                          type="color"
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Input
                          {...registerSecondaryColors('gradiant4.to')}
                          type="color"
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                      <span className="d-none">{`linear-gradient(${gradiant4?.from} , ${gradiant4?.to})`}</span>
                    </div>
                  </FormGroup>
                  <FormGroup label="Fall Color">
                    <Input
                      {...registerSecondaryColors('gradiant4.fallBackColor')}
                      type="color"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                </div>
                <div className="p-3 gap-3">
                  <FormGroup label="Gradient 5">
                    <div className="flex gap-2 w-full">
                      <FormGroup wrapperClassName="w-auto">
                        <div
                          style={{
                            background: `linear-gradient(${gradiant5?.from} , ${gradiant5?.to})`,
                            width: 30,
                            height: 30,
                            borderRadius: 25,
                            marginTop: 5,
                          }}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Input
                          {...registerSecondaryColors('gradiant5.from')}
                          type="color"
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Input
                          {...registerSecondaryColors('gradiant5.to')}
                          type="color"
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                      <span className="d-none">{`linear-gradient(${gradiant5?.from} , ${gradiant5?.to})`}</span>
                    </div>
                  </FormGroup>
                  <FormGroup label="Fall Color">
                    <Input
                      {...registerSecondaryColors('gradiant5.fallBackColor')}
                      type="color"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                </div>
                <div className="p-3 gap-3">
                  <FormGroup label="Gradient 6">
                    <div className="flex gap-2 w-full">
                      <FormGroup wrapperClassName="w-auto">
                        <div
                          style={{
                            background: `linear-gradient(${gradiant6?.from} , ${gradiant6?.to})`,
                            width: 30,
                            height: 30,
                            borderRadius: 25,
                            marginTop: 5,
                          }}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Input
                          {...registerSecondaryColors('gradiant6.from')}
                          type="color"
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Input
                          {...registerSecondaryColors('gradiant6.to')}
                          type="color"
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                      <span className="d-none">{`linear-gradient(${gradiant6?.from} , ${gradiant6?.to})`}</span>
                    </div>
                  </FormGroup>
                  <FormGroup label="Fall Color">
                    <Input
                      {...registerSecondaryColors('gradiant6.fallBackColor')}
                      type="color"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                </div>
                <div className="p-3 gap-3">
                  <FormGroup label="Gradient 7">
                    <div className="flex gap-2 w-full">
                      <FormGroup wrapperClassName="w-auto">
                        <div
                          style={{
                            background: `linear-gradient(${gradiant7?.from} , ${gradiant7?.to})`,
                            width: 30,
                            height: 30,
                            borderRadius: 25,
                            marginTop: 5,
                          }}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Input
                          {...registerSecondaryColors('gradiant7.from')}
                          type="color"
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Input
                          {...registerSecondaryColors('gradiant7.to')}
                          type="color"
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                      <span className="d-none">{`linear-gradient(${gradiant7?.from} , ${gradiant7?.to})`}</span>
                    </div>
                  </FormGroup>
                  <FormGroup label="Fall Color">
                    <Input
                      {...registerSecondaryColors('gradiant7.fallBackColor')}
                      type="color"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                </div>
              </div>
            </form>

            <div className="flex justify-end gap-2 p-3 border-t border-monochrome-120 mt-4">
              <Button
                onClick={() => navigate(-1)}
                variant="secondary"
                data-dismiss="modal"
                disabled={!hasEditPermission}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                form="settings-secondary-colors-form"
                type="submit"
                disabled={!hasEditPermission}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <p className="text-title-xxs text-accent-1 mb-2">Tertiary Colors</p>
          <div className="rounded-3xl border border-monochrome-120">
            <div className="p-3">
              <form
                id="settings-tertiary-colors-form"
                onSubmit={handleTertiaryColorsSubmit(submitTertiaryColors)}
              >
                <div className="flex flex-col gap-3">
                  <div className="p-3 gap-3">
                    <FormGroup label="Gradient Fade">
                      <div className="flex gap-2 w-full">
                        <FormGroup wrapperClassName="w-auto">
                          <div
                            style={{
                              background: `linear-gradient(${gradiantFade?.from} , ${gradiantFade?.to})`,
                              width: 30,
                              height: 30,
                              borderRadius: 25,
                              marginTop: 5,
                            }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Input
                            {...registerTertiaryColors('gradiantFade.from')}
                            type="color"
                            disabled={!hasEditPermission}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Input
                            {...registerTertiaryColors('gradiantFade.to')}
                            type="color"
                            disabled={!hasEditPermission}
                          />
                        </FormGroup>
                        <span className="d-none">{`linear-gradient(${gradiantFade?.from} , ${gradiantFade?.to})`}</span>
                      </div>
                    </FormGroup>
                    <FormGroup label="Fall Color">
                      <Input
                        {...registerTertiaryColors(
                          'gradiantFade.fallBackColor',
                        )}
                        type="color"
                        disabled={!hasEditPermission}
                      />
                    </FormGroup>
                  </div>
                  <div className="p-3 gap-3">
                    <FormGroup label="Gradient Menu">
                      <div className="flex gap-2 w-full">
                        <FormGroup wrapperClassName="w-auto">
                          <div
                            style={{
                              background: `linear-gradient(${gradiantMenu?.from} , ${gradiantMenu?.to})`,
                              width: 30,
                              height: 30,
                              borderRadius: 25,
                              marginTop: 5,
                            }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Input
                            {...registerTertiaryColors('gradiantMenu.from')}
                            type="color"
                            disabled={!hasEditPermission}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Input
                            {...registerTertiaryColors('gradiantMenu.to')}
                            type="color"
                            disabled={!hasEditPermission}
                          />
                        </FormGroup>
                        <span className="d-none">{`linear-gradient(${gradiantMenu?.from} , ${gradiantMenu?.to})`}</span>
                      </div>
                    </FormGroup>
                    <FormGroup label="Fall Color">
                      <Input
                        {...registerTertiaryColors(
                          'gradiantMenu.fallBackColor',
                        )}
                        type="color"
                        disabled={!hasEditPermission}
                      />
                    </FormGroup>
                  </div>
                </div>
              </form>
            </div>
            <div className="flex justify-end gap-2 p-3 border-t border-monochrome-120 mt-4">
              <Button
                onClick={() => navigate(-1)}
                variant="secondary"
                data-dismiss="modal"
                disabled={!hasEditPermission}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                form="settings-tertiary-colors-form"
                type="submit"
                disabled={!hasEditPermission}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
